/**
 * PrepareCommunityVideoThumbnailUseCase
 *
 * Client-side preparation of the community VIDEO thumbnail for upload. The full
 * video is handled by the server transcode + segment-encryption pipeline
 * (Tasks 1–8); ONLY the thumbnail is produced client-side here (the legacy CF
 * used to ffmpeg a single frame — that pass moves to the client, mirroring the
 * chat pipeline).
 *
 * KEY DIFFERENCE FROM THE CHAT SIBLING (`prepareChatVideoThumbnailUseCase.ts`):
 * community media is NOT E2EE (`useCommunity.ts:21`) — no peer key exists — so
 * the captured frame is paired with its upload path as a PLAINTEXT JPEG Blob.
 * There is no `EncryptBinaryUseCase` step.
 *
 * Orchestrates, in order:
 *   1. Capture a thumbnail frame from the source video. This reuses the shared
 *      `videoThumbnailService` — a generic offscreen-`<video>` + canvas frame
 *      capture (≈480 longest side, JPEG) shared with the chat pipeline.
 *   2. If a frame was produced, pair the plaintext JPEG blob with its
 *      deterministic upload path
 *      (`MediaUtils.getCommunityVideoThumbnailUploadPath` →
 *      `community/{owner}/thumbnail/{fileName}` — the SAME path the reworked
 *      `transcodeCommunityMessageVideo` CF reads and writes to `thumbnail`,
 *      Task 6).
 *   3. If NO frame was produced (the browser cannot decode the codec —
 *      consideration D), return `{ thumbnail: null, thumbnailUploadPath: null }`
 *      so the send flow SKIPS the thumbnail upload entirely. The message has no
 *      `thumbnail` and the media card renders the placeholder frame + play icon.
 *      A missing thumbnail must NOT abort the send.
 *
 * Web-first design — no Flutter parity (Flutter's community video pipeline is the
 * legacy server-side ffmpeg path).
 *
 * PURE DOMAIN: this use case does NOT upload, write Firestore, or dispatch. It
 * returns the plaintext thumbnail blob (or null) + its target path; the
 * presentation/data send flow (Task 11/16) performs the actual storage upload to
 * that path when present and then fires the `transcodeCommunityMessageVideo`
 * callable. The CF derives the same path server-side, so no path is carried in
 * the payload.
 */

import {
  VideoThumbnailService,
  videoThumbnailService as defaultVideoThumbnailService,
} from '@/domain/services/video/videoThumbnailService';
import { MediaUtils } from '@/domain/utils/mediaUtils';

/**
 * Result of preparing the community video thumbnail.
 *
 * The frame-produced case carries the PLAINTEXT JPEG blob + its deterministic
 * upload path; the codec-failure case carries `thumbnail: null` (and no path) so
 * the send flow skips the upload and the message has no `thumbnail`.
 */
export interface PreparedCommunityVideoThumbnail {
  /**
   * Plaintext thumbnail JPEG Blob, or `null` when the client could not produce a
   * frame (consideration D — codec fallback). Community media is not E2EE, so
   * this is NOT encrypted.
   */
  thumbnail: Blob | null;
  /**
   * RELATIVE bucket path the plaintext thumbnail MUST be uploaded to —
   * `community/{owner}/thumbnail/{fileName}`. The CF reads this verbatim.
   * `null` when no thumbnail was produced (nothing to upload).
   */
  thumbnailUploadPath: string | null;
}

export class PrepareCommunityVideoThumbnailUseCase {
  private readonly videoThumbnailService: VideoThumbnailService;

  constructor(
    videoThumbnailService: VideoThumbnailService = defaultVideoThumbnailService,
  ) {
    this.videoThumbnailService = videoThumbnailService;
  }

  /**
   * Produce the plaintext video thumbnail + its upload path, or the null
   * fallback when the codec cannot be decoded.
   *
   * @param source - The user-selected community video File/Blob.
   * @param baseUploadPath - The 3-segment `community/{owner}/{fileName}` path
   *   (the same value the send flow passes as the transcode payload's
   *   `filePath`). Used to derive the deterministic thumbnail path.
   * @returns `{ thumbnail, thumbnailUploadPath }` when a frame was captured;
   *   `{ thumbnail: null, thumbnailUploadPath: null }` on the codec fallback
   *   (NOT an error — the upload is skipped).
   */
  async execute(
    source: Blob,
    baseUploadPath: string,
  ): Promise<PreparedCommunityVideoThumbnail> {
    // (1) Capture the thumbnail frame. `null` ⇒ codec fallback (consideration D).
    const thumbnail = await this.videoThumbnailService.capture(source);
    if (thumbnail === null) {
      // No frame: skip the upload entirely. Do NOT derive a path.
      return { thumbnail: null, thumbnailUploadPath: null };
    }

    // (2) Pair the PLAINTEXT JPEG with the deterministic path the CF (Task 6)
    // reads and writes to `thumbnail`. No encryption — community is not E2EE.
    const thumbnailUploadPath =
      MediaUtils.getCommunityVideoThumbnailUploadPath(baseUploadPath);

    return { thumbnail, thumbnailUploadPath };
  }
}

/** Shared singleton — mirrors the other use-case singletons. */
export const prepareCommunityVideoThumbnailUseCase =
  new PrepareCommunityVideoThumbnailUseCase();
